import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Flag, Loader2, Utensils, XCircle } from "lucide-react";
import { portalFetch, KyraApiError } from "@/lib/api";
import { usePlan } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { MealItemRow } from "@/components/exec-diet/MealItemRow";
import { DietItemModal } from "@/components/exec-diet/DietItemModal";
import { AbandonConfirm } from "@/components/exec-diet/AbandonConfirm";
import type {
  DietExecutionDetailResponse,
  StudentDiet,
  StudentDietExecutionItem,
  StudentDietItem,
  UpdateDietExecutionItemPayload,
  UpdateDietExecutionItemResponse,
} from "@/lib/types";

/** Item prescrito achatado, com o nome do bloco a que pertence. */
interface FlatItem {
  blockName: string;
  blockId: string;
  item: StudentDietItem;
}

function flattenDiet(diet: StudentDiet): FlatItem[] {
  const out: FlatItem[] = [];
  for (const block of diet.blocks) {
    for (const item of block.items) {
      out.push({ blockName: block.name, blockId: block.id, item });
    }
  }
  return out;
}

/**
 * Tela full-screen de registro de uma refeição (dieta). Carrega a execução
 * (GET /me/diet-executions/:id) e cruza com o plano (usePlan) pra exibir os
 * alimentos prescritos. Cada item abre um modal; o aluno marca o que comeu e
 * o status. Ao final, finaliza ou abandona e volta pro painel.
 */
export function DietExecutionView() {
  const { slug = "", executionId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["diet-execution", executionId],
    enabled: executionId.length > 0,
    staleTime: 0,
    queryFn: ({ signal }) =>
      portalFetch<DietExecutionDetailResponse>(
        `/student-portal/me/diet-executions/${executionId}`,
        { signal },
      ),
  });
  const plan = usePlan();

  // Estado local imutável dos itens registrados, indexado por diet_item_id.
  const [itemsByDietItemId, setItemsByDietItemId] = useState<Map<
    string,
    StudentDietExecutionItem
  > | null>(null);

  const [editingDietItemId, setEditingDietItemId] = useState<string | null>(null);
  const [closing, setClosing] = useState<"finishing" | "abandoning" | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  // Semeia o estado local a partir da resposta da API (uma vez).
  const seeded = itemsByDietItemId !== null;
  if (!seeded && detail.data) {
    const map = new Map<string, StudentDietExecutionItem>();
    for (const it of detail.data.items) map.set(it.diet_item_id, it);
    setItemsByDietItemId(map);
  }
  const itemsMap = itemsByDietItemId ?? new Map<string, StudentDietExecutionItem>();

  // Dieta prescrita correspondente a esta execução.
  const diet = useMemo<StudentDiet | null>(() => {
    if (!detail.data || !plan.data) return null;
    return plan.data.diets.find((d) => d.id === detail.data.execution.diet_id) ?? null;
  }, [detail.data, plan.data]);

  const flatItems = useMemo(() => (diet ? flattenDiet(diet) : []), [diet]);
  const totalItems = flatItems.length;

  const completedCount = useMemo(() => {
    let n = 0;
    for (const fi of flatItems) {
      if (itemsMap.get(fi.item.id)?.completed_at) n += 1;
    }
    return n;
  }, [flatItems, itemsMap]);

  const handleSaveItem = useCallback(
    async (
      dietItemId: string,
      payload: UpdateDietExecutionItemPayload,
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await portalFetch<UpdateDietExecutionItemResponse>(
          `/student-portal/me/diet-executions/${executionId}/items/${dietItemId}`,
          { method: "PATCH", body: payload },
        );
        setItemsByDietItemId((prev) => {
          const next = new Map(prev ?? []);
          next.set(dietItemId, res.item);
          return next;
        });
        return { ok: true };
      } catch (err) {
        if (err instanceof KyraApiError && err.status === 401) {
          navigate(`/s/${slug}/login`, { replace: true });
          return { ok: false, error: "session_expired" };
        }
        console.error("[DietExecutionView] PATCH item falhou", err);
        return {
          ok: false,
          error:
            err instanceof KyraApiError
              ? `Não consegui salvar (status ${err.status}).`
              : "Não consegui salvar. Verifique sua conexão.",
        };
      }
    },
    [executionId, navigate, slug],
  );

  async function handleFinish() {
    if (closing) return;
    setClosing("finishing");
    setCloseError(null);
    try {
      await portalFetch(`/student-portal/me/diet-executions/${executionId}/finish`, {
        method: "POST",
        body: {},
      });
      await queryClient.invalidateQueries({ queryKey: ["history", "diet"] });
      navigate(`/s/${slug}?flash=diet_finished`, { replace: true });
    } catch (err) {
      if (err instanceof KyraApiError && err.status === 401) {
        navigate(`/s/${slug}/login`, { replace: true });
        return;
      }
      console.error("[DietExecutionView] finish falhou", err);
      setCloseError("Não consegui finalizar agora. Tente de novo.");
      setClosing(null);
    }
  }

  async function handleAbandon() {
    if (closing) return;
    setClosing("abandoning");
    setCloseError(null);
    try {
      await portalFetch(`/student-portal/me/diet-executions/${executionId}/abandon`, {
        method: "POST",
        body: {},
      });
      await queryClient.invalidateQueries({ queryKey: ["history", "diet"] });
      navigate(`/s/${slug}`, { replace: true });
    } catch (err) {
      if (err instanceof KyraApiError && err.status === 401) {
        navigate(`/s/${slug}/login`, { replace: true });
        return;
      }
      console.error("[DietExecutionView] abandon falhou", err);
      setCloseError("Não consegui abandonar agora. Tente de novo.");
      setClosing(null);
    }
  }

  const editingFlat = useMemo(() => {
    if (!editingDietItemId) return null;
    return flatItems.find((fi) => fi.item.id === editingDietItemId) ?? null;
  }, [editingDietItemId, flatItems]);
  const editingExisting = editingDietItemId ? itemsMap.get(editingDietItemId) ?? null : null;

  // ── Loading / erro ──────────────────────────────────────────────────────
  if (detail.isLoading || plan.isLoading) return <Spinner fullscreen />;

  if (detail.isError) {
    const is404 =
      detail.error instanceof KyraApiError && detail.error.status === 404;
    return (
      <div className="px-5 pt-8">
        <EmptyState
          icon={Utensils}
          title={is404 ? "Registro não encontrado" : "Não consegui carregar"}
          description={
            is404
              ? "Esse registro de refeição não existe mais ou não é seu."
              : "Algo deu errado ao abrir o registro. Tente de novo em instantes."
          }
        />
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => navigate(`/s/${slug}`)}
            className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-elevated px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-hover"
          >
            <ArrowLeft size={14} aria-hidden /> Voltar pro painel
          </button>
        </div>
      </div>
    );
  }

  const progressPct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  return (
    <div className="flex min-h-dvh flex-col">
      {/* HEADER */}
      <header className="sticky top-0 z-20 border-b border-border-subtle bg-canvas/85 backdrop-blur-md">
        <div className="flex items-center gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={() => navigate(`/s/${slug}`)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-elevated text-muted transition-colors hover:border-border-strong hover:bg-surface-hover hover:text-ink"
            aria-label="Voltar pro painel"
          >
            <ArrowLeft size={15} aria-hidden />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-base text-ink sm:text-lg">
              {diet?.title ?? "Refeição"}
            </h1>
            <p className="truncate text-[11px] uppercase tracking-wider text-faint">
              Refeição em registro
            </p>
          </div>
        </div>

        {/* Barra de progresso */}
        {totalItems > 0 ? (
          <div className="px-4 pb-3 sm:px-6">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-elevated">
              <div
                className="h-full rounded-full bg-accent-soft transition-[width] duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
                role="progressbar"
                aria-valuenow={completedCount}
                aria-valuemin={0}
                aria-valuemax={totalItems}
                aria-label="Progresso do registro"
              />
            </div>
          </div>
        ) : null}
      </header>

      {/* LISTA — pb extra pra não cobrir com a barra de ação + bottom nav */}
      <main className="flex-1 px-4 py-5 pb-44 sm:px-6 sm:py-8">
        {totalItems === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-subtle bg-surface/40 p-8 text-center text-sm text-muted">
            Esta dieta não tem alimentos cadastrados. Avise seu treinador.
          </div>
        ) : (
          <ItemList
            flatItems={flatItems}
            itemsMap={itemsMap}
            onEdit={(id) => setEditingDietItemId(id)}
            disabled={closing !== null}
          />
        )}

        {closeError ? (
          <div
            role="alert"
            className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
          >
            {closeError}
          </div>
        ) : null}
      </main>

      {/* BARRA DE AÇÃO — fixa, acima do bottom nav do AppShell */}
      <footer className="fixed inset-x-0 bottom-0 z-30 border-t border-border-subtle bg-canvas/95 backdrop-blur-md">
        <div
          className="mx-auto flex max-w-lg flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-1.5 text-sm text-muted">
            <span className="text-ink">{completedCount}</span>
            <span>de</span>
            <span className="text-ink">{totalItems}</span>
            <span>{totalItems === 1 ? "alimento marcado" : "alimentos marcados"}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmAbandon(true)}
              disabled={closing !== null}
              className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-elevated px-3 py-2 text-sm font-medium text-muted transition-colors hover:border-danger/60 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Abandonar registro de refeição"
            >
              <XCircle size={14} aria-hidden />
              Abandonar
            </button>
            <button
              type="button"
              onClick={handleFinish}
              disabled={closing !== null}
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Concluir refeição"
            >
              {closing === "finishing" ? (
                <>
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                  Finalizando…
                </>
              ) : (
                <>
                  <Flag size={14} aria-hidden />
                  Concluir refeição
                </>
              )}
            </button>
          </div>
        </div>
      </footer>

      {editingFlat ? (
        <DietItemModal
          open
          onClose={() => setEditingDietItemId(null)}
          dietItem={editingFlat.item}
          existing={editingExisting}
          onSave={handleSaveItem}
        />
      ) : null}

      {confirmAbandon ? (
        <AbandonConfirm
          onCancel={() => setConfirmAbandon(false)}
          onConfirm={() => {
            setConfirmAbandon(false);
            handleAbandon();
          }}
          loading={closing === "abandoning"}
        />
      ) : null}
    </div>
  );
}

// ── Lista com cabeçalhos de bloco ─────────────────────────────────────────
interface ItemListProps {
  flatItems: FlatItem[];
  itemsMap: Map<string, StudentDietExecutionItem>;
  onEdit: (dietItemId: string) => void;
  disabled: boolean;
}

function ItemList({ flatItems, itemsMap, onEdit, disabled }: ItemListProps) {
  const rows: Array<
    | { kind: "header"; blockId: string; blockName: string }
    | { kind: "item"; flat: FlatItem }
  > = [];
  let lastBlockId: string | null = null;
  for (const fi of flatItems) {
    if (fi.blockId !== lastBlockId) {
      rows.push({ kind: "header", blockId: fi.blockId, blockName: fi.blockName });
      lastBlockId = fi.blockId;
    }
    rows.push({ kind: "item", flat: fi });
  }

  return (
    <div className="animate-fade-in space-y-4">
      {rows.map((row, idx) =>
        row.kind === "header" ? (
          <h2
            key={`h-${row.blockId}-${idx}`}
            className="text-xs font-medium uppercase tracking-wider text-accent-soft"
          >
            {row.blockName}
          </h2>
        ) : (
          <MealItemRow
            key={row.flat.item.id}
            item={row.flat.item}
            existing={itemsMap.get(row.flat.item.id) ?? null}
            disabled={disabled}
            onEdit={() => onEdit(row.flat.item.id)}
          />
        ),
      )}
    </div>
  );
}
